import { useEffect, useRef, useState } from 'react'

export interface ProgressBarProps {
  /** Target percentage; also written to `data-ul-progress-value`. */
  value: number
  /** Extra class on the container, e.g. `donation-progress-container`. */
  containerClassName?: string
  /** Extra class on the bar itself, e.g. `donation-progressbar` or `skill-progressbar`. */
  barClassName?: string
  /** Extra class on the label, e.g. `donation-progress-label`. */
  labelClassName?: string
  /** Whether to print the percentage inside the bar. */
  showLabel?: boolean
}

/**
 * Count-up progress bar. Used on the Donations (8), Team Details (3) and Donation
 * Details (1) pages.
 *
 * The bar starts at 0 and counts up one percent per animation frame the first time it
 * scrolls into view, then stops observing.
 *
 * Both variants share `.ul-progress-container` / `.ul-progressbar` /
 * `.ul-progress-label` and differ only by an extra page-specific class, which is why
 * those arrive as props. The label text is written by this component.
 */
export function ProgressBar({
  value,
  containerClassName,
  barClassName,
  labelClassName,
  showLabel = true,
}: ProgressBarProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Honour reduced-motion by jumping straight to the value.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setCurrent(value)
      return
    }

    let frame = 0
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        observer.unobserve(entry.target)
        let n = 0
        const tick = () => {
          if (n >= value) return
          n += 1
          setCurrent(n)
          frame = requestAnimationFrame(tick)
        }
        frame = requestAnimationFrame(tick)
      }
    })

    observer.observe(el)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [value])

  const containerClasses = ['ul-progress-container']
  if (containerClassName) containerClasses.unshift(containerClassName)
  const barClasses = ['ul-progressbar']
  if (barClassName) barClasses.unshift(barClassName)
  const labelClasses = ['ul-progress-label']
  if (labelClassName) labelClasses.unshift(labelClassName)

  return (
    <div className={containerClasses.join(' ')}>
      <div
        ref={ref}
        className={barClasses.join(' ')}
        data-ul-progress-value={value}
        style={{ width: `${current}%` }}
      >
        <div className={labelClasses.join(' ')}>{showLabel ? `${current}%` : null}</div>
      </div>
    </div>
  )
}
